import { useClientPortalAPI, useSubmit } from "@certn/api";
import {
  BasicButton,
  ButtonGroup,
  Card,
  Container,
  Loader,
  PrimaryButton,
  Divider,
  Text,
  showToast,
  TextInputFormikField,
} from "@certn/cascadia-react";
import { regexTypes } from "@certn/utils";
import { css } from "@emotion/css";
import { Form, Formik } from "formik";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { array, object, string } from "yup";

import { EmailListField } from "./EmailListField";

interface ClientBillingProfileProps {
  readOnly: boolean;
}

export function ClientBillingProfileContactInformationForm({
  readOnly,
}: ClientBillingProfileProps) {
  const { t } = useTranslation();
  const { billingProfileId } = useParams();

  const { data: billingProfile, mutate } = useClientPortalAPI(
    `/api/internal/client-portal/billing-profiles/${billingProfileId}/`
  );
  const { submit } = useSubmit(
    `/api/internal/client-portal/billing-profiles/${billingProfileId}/update/`,
    {
      showToastOnError: true,
      onSuccess: async () => {
        await mutate().catch(() => {});
        showToast(t("Contact information updated"), { type: "success" });
      },
    }
  );

  const schema = object().shape({
    contact_first_name: string().required().label(t("Given name")),
    contact_last_name: string().required().label(t("Family name")),
    contact_email: string().email().required().label(t("Email")),
    contact_phone: string().nullable().label(t("Phone number")),
    cc_emails: array()
      .of(string())
      .test(
        "invalidEmail",
        t("Please check all the entries are valid emails"),
        (emails) => {
          return emails?.every((item) => {
            return string()
              .email()
              .matches(regexTypes.checkMultipleEmails)
              .isValidSync(item);
          });
        }
      ),
  });

  if (!billingProfile) {
    return <Loader />;
  }

  return (
    <Card className={styles.card}>
      <Formik
        initialValues={billingProfile}
        onSubmit={async (values) => submit(values)}
        validationSchema={schema}
      >
        {({ isSubmitting, dirty }) => {
          return (
            <Form>
              <Container>
                <Text style="form/section-heading">
                  {t("Primary contact information")}
                </Text>
                <TextInputFormikField
                  name="contact_first_name"
                  label={t("Given name")}
                  isRequired
                  isDisabled={readOnly}
                  testID="billing-information-first-name"
                />
                <TextInputFormikField
                  name="contact_last_name"
                  label={t("Family name")}
                  isRequired
                  isDisabled={readOnly}
                  testID="billing-information-last-name"
                />
                <TextInputFormikField
                  name="contact_phone"
                  label={t("Phone")}
                  isDisabled={readOnly}
                  testID="billing-information-phone"
                />
                <TextInputFormikField
                  name="contact_email"
                  label={t("Email")}
                  isRequired
                  isDisabled={readOnly}
                  testID="billing-information-email"
                />
                <Divider />
                <Text style="form/section-heading">
                  {t("Additional invoice recipients")}
                </Text>
                <EmailListField
                  label={t("Email(s)")}
                  name="cc_emails"
                  isDisabled={readOnly}
                  isRequired={false}
                  testID="enter-applicants-page-recipient-emails"
                  description={t(
                    "To send to multiple recipients, separate each email with a comma or space."
                  )}
                />
                {!readOnly && (
                  <ButtonGroup>
                    <BasicButton
                      type="reset"
                      size="medium"
                      testID="cancel-button"
                      isDisabled={!dirty}
                    >
                      {t("Reset")}
                    </BasicButton>
                    <PrimaryButton
                      type="submit"
                      size="medium"
                      testID="save-billing-profile-button"
                      isLoading={isSubmitting}
                      isDisabled={!dirty}
                    >
                      {t("Save")}
                    </PrimaryButton>
                  </ButtonGroup>
                )}
              </Container>
            </Form>
          );
        }}
      </Formik>
    </Card>
  );
}

const styles = {
  card: css({
    borderWidth: "var(--space-025)",
    borderRadius: "var(--border-radius-3)",
    padding: "var(--space-5)",
  }),
};
